import { Link } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import cn from './cn.js';

/** Breadcrumbs: items [{ label, to }] – the last one is the current page. */
export function Breadcrumbs({ items = [], className }) {
  if (!items.length) return null;
  return (
    <nav aria-label="Breadcrumb" className={cn('flex', className)}>
      <ol className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm">
        {items.map((item, index) => {
          const last = index === items.length - 1;
          return (
            <li key={`${item.label}-${index}`} className="flex items-center gap-x-1.5 min-w-0">
              {index > 0 && <ChevronRightIcon className="h-4 w-4 shrink-0 text-gray-300" aria-hidden="true" />}
              {last || !item.to ? (
                <span className={cn('truncate', last ? 'font-medium text-gray-900' : 'text-gray-500')} aria-current={last ? 'page' : undefined}>
                  {item.label}
                </span>
              ) : (
                <Link to={item.to} className="truncate text-gray-500 transition-colors hover:text-gray-900">
                  {item.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

/**
 * PageHeader: title, description, breadcrumbs, metadata row and actions.
 */
export default function PageHeader({ title, description, actions, breadcrumbs, eyebrow, meta, className, children, size = 'lg', spacing = true }) {
  return (
    <div className={cn(spacing && 'mb-6 sm:mb-8', className)}>
      {breadcrumbs && <Breadcrumbs items={breadcrumbs} className="mb-3" />}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 flex-1">
          {eyebrow && <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-indigo-600">{eyebrow}</p>}
          {title && (
            <h1 className={cn('font-semibold tracking-tight text-gray-900', size === 'lg' ? 'text-2xl sm:text-3xl' : 'text-xl sm:text-2xl', 'truncate')}>
              {title}
            </h1>
          )}
          {description && <p className="mt-1.5 max-w-3xl text-sm text-gray-500">{description}</p>}
          {meta && <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-gray-500">{meta}</div>}
          {children}
        </div>
        {actions && <div className="flex shrink-0 flex-wrap items-center gap-2 lg:ml-6">{actions}</div>}
      </div>
    </div>
  );
}

/** A metadata item (icon + text) for PageHeader.meta rows. */
export function MetaItem({ icon: Icon, children, className, title }) {
  return (
    <span className={cn('inline-flex items-center gap-1.5', className)} title={title}>
      {Icon && <Icon className="h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />}
      {children}
    </span>
  );
}
